export type PublicDocumentLineItem = {
  label: string;
  quantity: number;
  unitPrice: number;
};

export type PublicOrderSample = {
  orderNumber: string;
  orderDate: string;
  clientName: string;
  clientEmail: string;
  clientCompany: string;
  serviceName: string;
  amount: number;
  currencyCode: string;
  paymentMethod: string;
  invoiceNumber: string;
  quoteNumber: string;
};

export type PublicInvoiceSample = {
  invoiceNumber: string;
  invoiceDate: string;
  clientName: string;
  clientEmail: string;
  clientCompany: string;
  serviceName: string;
  currencyCode: string;
  paymentMethod: string;
  items: PublicDocumentLineItem[];
  taxAmount: number;
};

export type PublicQuoteSample = {
  quoteNumber: string;
  quoteDate: string;
  quoteValidUntil: string;
  clientName: string;
  clientEmail: string;
  clientCompany: string;
  serviceName: string;
  currencyCode: string;
  items: PublicDocumentLineItem[];
  taxAmount: number;
  scope: string[];
};

const defaultOrderSample: PublicOrderSample = {
  orderNumber: 'CMD-2026-0001',
  orderDate: '2026-04-23',
  clientName: 'Jean Dupont',
  clientEmail: 'jean.dupont@example.com',
  clientCompany: 'Entreprise Demo',
  serviceName: 'Tunnel de vente premium',
  amount: 441,
  currencyCode: 'CAD',
  paymentMethod: 'Carte bancaire',
  invoiceNumber: 'FAC-2026-0001',
  quoteNumber: 'DEV-2026-0001',
};

const defaultInvoiceItems: PublicDocumentLineItem[] = [
  {
    label: 'Tunnel de vente premium',
    quantity: 1,
    unitPrice: 441,
  },
];

const defaultQuoteItems: PublicDocumentLineItem[] = [
  {
    label: 'Tunnel de vente premium',
    quantity: 1,
    unitPrice: 441,
  },
];

const orderSamples: Record<string, PublicOrderSample> = {
  'CMD-2026-0001': defaultOrderSample,
};

const invoiceSamples: Record<string, PublicInvoiceSample> = {
  'FAC-2026-0001': {
    invoiceNumber: 'FAC-2026-0001',
    invoiceDate: '2026-04-23',
    clientName: 'Jean Dupont',
    clientEmail: 'jean.dupont@example.com',
    clientCompany: 'Entreprise Demo',
    serviceName: 'Tunnel de vente premium',
    currencyCode: 'CAD',
    paymentMethod: 'Carte bancaire',
    items: defaultInvoiceItems,
    taxAmount: 0,
  },
};

const quoteSamples: Record<string, PublicQuoteSample> = {
  'DEV-2026-0001': {
    quoteNumber: 'DEV-2026-0001',
    quoteDate: '2026-04-23',
    quoteValidUntil: '2026-04-30',
    clientName: 'Jean Dupont',
    clientEmail: 'jean.dupont@example.com',
    clientCompany: 'Entreprise Demo',
    serviceName: 'Tunnel de vente premium',
    currencyCode: 'CAD',
    items: defaultQuoteItems,
    taxAmount: 0,
    scope: [
      'Cadrage et architecture du parcours',
      'Création du tunnel premium et de ses contenus',
      'Mise en place des automatismes essentiels',
      'Support de lancement et recommandations',
    ],
  },
};

const normalizeKey = (value: string | undefined, fallback: string) => {
  const trimmedValue = (value || '').trim();
  return trimmedValue ? trimmedValue.toUpperCase() : fallback;
};

export const getPublicOrderSample = (orderNumber?: string): PublicOrderSample => {
  const resolvedOrderNumber = normalizeKey(orderNumber, defaultOrderSample.orderNumber);
  return (
    orderSamples[resolvedOrderNumber] || {
      ...defaultOrderSample,
      orderNumber: resolvedOrderNumber,
      invoiceNumber: `FAC-${resolvedOrderNumber.replace(/^CMD-/, '')}`,
      quoteNumber: `DEV-${resolvedOrderNumber.replace(/^CMD-/, '')}`,
    }
  );
};

export const getPublicInvoiceSample = (invoiceNumber?: string): PublicInvoiceSample => {
  const resolvedInvoiceNumber = normalizeKey(invoiceNumber, 'FAC-2026-0001');
  return (
    invoiceSamples[resolvedInvoiceNumber] || {
      ...invoiceSamples['FAC-2026-0001'],
      invoiceNumber: resolvedInvoiceNumber,
    }
  );
};

export const getPublicQuoteSample = (quoteNumber?: string): PublicQuoteSample => {
  const resolvedQuoteNumber = normalizeKey(quoteNumber, 'DEV-2026-0001');
  return (
    quoteSamples[resolvedQuoteNumber] || {
      ...quoteSamples['DEV-2026-0001'],
      quoteNumber: resolvedQuoteNumber,
    }
  );
};

export const calculateDocumentSubtotal = (items: PublicDocumentLineItem[]) =>
  items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
